import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { useQuerystring } from "$routes/_lib/use-querystring";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@material-ui/core";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InvoiceStatus } from "../InvoiceFilters";
import { InvoiceDetails } from "./InvoiceDetails";
import { InvoiceSummary } from "./InvoiceSummary";
import type { InvoiceCardProps, InvoiceCardSummaryProps } from "./types";

export const InvoiceCard = ({
  invoiceId,
  invoiceSummary,
  priceList,
  insurers,
  balance,
}: InvoiceCardProps) => {
  const visibleRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isDraft = invoiceSummary.invoiceStatus === InvoiceStatus.Draft;
  const inTheLast10Minutes = dayjs(new Date()).subtract(10, "minute");
  const isNewInvoice = dayjs(invoiceSummary.created) > inTheLast10Minutes;
  const currency = useCurrencyFormat();
  const convertFromPence = useConvertFromPenceFactory();

  const query = useQuerystring();
  const [expanded, setExpanded] = useState<boolean>(
    (isDraft && isNewInvoice) || invoiceId === query?.invoiceId,
  );

  const [summaryBalance, setSummaryBalance] = useState<string>(
    currency.format(convertFromPence(balance.due)),
  );
  const [summaryTotal, setSummaryTotal] = useState<string>(
    currency.format(convertFromPence(balance.total)),
  );

  // intentionally left the dependency array to be empty
  // so it keeps the component into view while all invoices are loading
  // until the last rerendering is done and their position is final
  useEffect(() => {
    if (invoiceId === query.invoiceId) {
      if (visibleRef.current) {
        visibleRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, []);

  const handleChange = (_event: unknown, hasExpanded: boolean) => {
    const { pathname, search } = location;
    const params = new URLSearchParams(search);
    params.set("invoiceId", invoiceId);
    if (hasExpanded) {
      navigate({ pathname, search: params.toString() }, { replace: true });
    }
  };

  const invoiceSummaryProps: InvoiceCardSummaryProps = {
    ...invoiceSummary,
    balance: summaryBalance,
    total: summaryTotal,
    expanded,
    setExpanded,
  };

  return (
    <div
      ref={visibleRef}
      data-testid={`invoice-card-${invoiceId.toLowerCase()}`}
    >
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        expanded={expanded}
        onChange={handleChange}
        elevation={4}
      >
        <AccordionSummary>
          <InvoiceSummary {...invoiceSummaryProps} />
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceDetails
            invoiceId={invoiceId}
            priceList={priceList}
            insurers={insurers}
            setSummaryBalance={setSummaryBalance}
            setSummaryTotal={setSummaryTotal}
            isNewInvoice={isNewInvoice}
            invoiceStatus={invoiceSummary.invoiceStatus}
            facility={invoiceSummary.facility}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
